import { AfterViewInit, Component, OnInit, QueryList, ViewChildren } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import * as _moment from 'moment';
import { forkJoin } from 'rxjs';
import { first } from 'rxjs/operators';
import { CampaignAction, CampaignLaunchMethod, CampaignStatus } from 'src/app/models/campaign';
import { SupplierService } from 'src/app/services/supplier.service';
import { LoadingOverlayService } from 'src/app/shared/loading-overlay/loading-overlay.service';
import { environment } from '../../../../environments/environment';
import { AuthenticationService } from '../../../core/authentication/authentication.service';
import { AppConfig } from '../../../core/configs/app.config';
import { CampaignService } from '../../../services/campaign.service';
import { CampaignMediaService } from 'src/app/services/campaign-media.service';
import { DownloadService } from '../../../services/download.service';
import { AlertService } from '../../../shared/alert/alert.service';
import { formatTitleCase } from 'src/app/shared/utility/common';
import { CampaignAddChargesModalComponent } from '../campaign-add-charges-modal/campaign-add-charges-modal.component';
import { CampaignCancelModalComponent } from '../campaign-cancel-modal/campaign-cancel-modal.component';
import { CampaignDeleteModalComponent } from '../campaign-delete-modal/campaign-delete-modal.component';
import { CampaignEditUserModalComponent } from '../campaign-edit-user-modal/campaign-edit-user-modal.component';
import { CampaignPauseEditVisualsModalComponent } from '../campaign-pause-edit-visuals-modal/campaign-pause-edit-visuals-modal.component';
import { CampaignPauseModalComponent } from '../campaign-pause-modal/campaign-pause-modal.component';
import { CampaignRejectModalComponent } from '../campaign-reject-modal/campaign-reject-modal.component';
import { CampaignRelaunchModalComponent } from '../campaign-relaunch-modal/campaign-relaunch-modal.component';
import { CampaignStopModalComponent } from '../campaign-stop-modal/campaign-stop-modal.component';

const moment = _moment;

export interface PeriodicElement {
    title: string;
    startDate: string;
    endDate: string;
    status: string;
    price: string;
}

@Component({
    selector: 'app-campaigns-overview',
    templateUrl: './campaigns-overview.component.html',
    styleUrls: ['./campaigns-overview.component.scss']
})
export class CampaignsOverviewComponent implements OnInit, AfterViewInit {
    public form: FormGroup;
    public formLiveApproved: FormGroup;
    public formPending: FormGroup;
    public formRejected: FormGroup;
    public formDraft: FormGroup;
    public formPausedCancelled: FormGroup;
    public formCancelledStoppedCompleted: FormGroup;
    public status = {};
    clientColumns: string[] = ['title', 'startDate', 'endDate', 'status', 'price', 'actions'];
    supplierPendingColumns: string[] = [
        'title',
        'clientName',
        'startDate',
        'endDate',
        'status',
        'price',
        'charges',
        'actions'
    ];
    supplierUpcomingStatusColumns: string[] = [
        'title',
        'clientName',
        'startDate',
        'endDate',
        'status',
        'upcomingStatus',
        'price',
        'charges',
        'actions'
    ];
    supplierRejectedColumns: string[] = [
        'title',
        'clientName',
        'startDate',
        'endDate',
        'status',
        'lastRejectReason',
        'price',
        'charges',
        'actions'
    ];
    adminColumns: string[] = [
        'title',
        'startDate',
        'endDate',
        'status',
        'price',
        'createdBy',
        'actions'
    ];
    adminRejectedColumns: string[] = [
        'title',
        'startDate',
        'endDate',
        'status',
        'lastRejectReason',
        'price',
        'createdBy',
        'actions'
    ];

    dataSource = { dataSource: null };
    dataSourceLiveApproved = { dataSource: null };
    dataSourcePending = { dataSource: null };
    dataSourceDraft = { dataSource: null };
    dataSourceRejected = { dataSource: null };
    dataSourcePausedCancelled = { dataSource: null };
    dataSourceCancelledStoppedCompleted = { dataSource: null };

    alertOptions = {
        autoClose: true,
        keepAfterRouteChange: false
    };

    @ViewChildren(MatPaginator) paginator = new QueryList<MatPaginator>();
    @ViewChildren(MatSort) sort = new QueryList<MatSort>();

    isAdmin = false;
    isSupplier = false;
    campaignStatus = CampaignStatus;
    rejectReason = '';
    userSort: Sort = { active: 'clientUpdatedAt', direction: 'desc' };
    liveApprovedSort: Sort = { active: 'clientUpdatedAt', direction: 'desc' };
    pendingSort: Sort = { active: 'clientUpdatedAt', direction: 'desc' };
    rejectedSort: Sort = { active: 'clientUpdatedAt', direction: 'desc' };
    draftSort: Sort = { active: 'clientUpdatedAt', direction: 'desc' };
    pausedCancelledSort: Sort = { active: 'clientUpdatedAt', direction: 'desc' };
    cancelledStoppedCompletedSort: Sort = { active: 'clientUpdatedAt', direction: 'desc' };
    onLoad = false;
    supplierTables = [
        {
            title: 'Needs Action',
            form: { name: 'formPending', value: null },
            dataSource: null,
            filterValues: 'filterValuesPending',
            supplierColumns: this.supplierPendingColumns
        },
        {
            title: 'Active Campaigns',
            form: { name: 'formLiveApproved', value: null },
            dataSource: null,
            filterValues: 'filterValuesLiveApproved',
            supplierColumns: this.supplierUpcomingStatusColumns,
            multipleStatus: [
                { name: 'APPROVED', color: '#28A745' },
                { name: 'LIVE', color: '#00C851' }
            ]
        },
        {
            title: 'Paused Campaigns',
            form: { name: 'formPausedCancelled', value: null },
            dataSource: null,
            filterValues: 'filterValuesPausedCancelled',
            supplierColumns: this.supplierUpcomingStatusColumns
        },
        {
            title: 'Completed / Stopped / Cancelled Campaigns',
            form: { name: 'formCancelledStoppedCompleted', value: null },
            dataSource: null,
            filterValues: 'filterValuesCancelledStoppedCompleted',
            supplierColumns: this.supplierUpcomingStatusColumns,
            multipleStatus: [
                { name: 'CANCELLED', color: '#D3D3D3' },
                { name: 'STOPPED', color: '#FD7E14' },
                { name: 'COMPLETED', color: '#343A40' }
            ]
        },
        {
            title: 'Rejected Campaigns',
            form: { name: 'formRejected', value: null },
            dataSource: null,
            filterValues: 'filterValuesRejected',
            supplierColumns: this.supplierRejectedColumns
        }
    ];

    constructor(
        private loadingOverlayService: LoadingOverlayService,
        private formBuilder: FormBuilder,
        private campaignService: CampaignService,
        private campaignMediaService: CampaignMediaService,
        private supplierService: SupplierService,
        private alertService: AlertService,
        private router: Router,
        private authenticationService: AuthenticationService,
        public dialog: MatDialog,
        private downloads: DownloadService
    ) {
        this.status[CampaignStatus.APPROVED] = {
            value: CampaignStatus.APPROVED,
            label: 'APPROVED',
            realLabel: 'APPROVED',
            color: '#28A745'
        };
        this.status[CampaignStatus.PARTIALLY_APPROVED] = {
            value: CampaignStatus.PARTIALLY_APPROVED,
            label: 'PARTIALLY APPROVED',
            realLabel: 'PARTIALLY APPROVED',
            color: '#70c21f'
        };
        this.status[CampaignStatus.PAUSED_PENDING_SUP_APPROVALS] = {
            value: CampaignStatus.PAUSED_PENDING_SUP_APPROVALS,
            label: 'AUTO PAUSED',
            realLabel: 'AUTO PAUSED',
            color: '#F2BA0F'
        };
        this.status[CampaignStatus.REJECTED] = {
            value: CampaignStatus.REJECTED,
            label: 'REJECTED',
            realLabel: 'REJECTED',
            color: '#DC3545'
        };
        this.status[CampaignStatus.PARTIALLY_REJECTED] = {
            value: CampaignStatus.PARTIALLY_REJECTED,
            label: 'PARTIALLY REJECTED',
            realLabel: 'PARTIALLY REJECTED',
            color: '#E55347'
        };
        this.status[CampaignStatus.PARTIALLY_LIVE] = {
            value: CampaignStatus.PARTIALLY_LIVE,
            label: 'PARTIALLY LIVE',
            realLabel: 'PARTIALLY LIVE',
            color: '#66D9EF'
        };
        this.status[CampaignStatus.DRAFT] = {
            value: CampaignStatus.DRAFT,
            label: 'DRAFT',
            realLabel: 'DRAFT',
            color: '#A0A0A0'
        };
        this.status[CampaignStatus.PENDING_APPROVAL] = {
            value: CampaignStatus.PENDING_APPROVAL,
            label: 'PENDING APPROVAL',
            realLabel: 'PENDING APPROVAL',
            color: '#007BFF'
        };
        this.status[CampaignStatus.PENDING_REAPPROVAL] = {
            value: CampaignStatus.PENDING_REAPPROVAL,
            label: 'PENDING RE-APPROVAL',
            realLabel: 'PENDING RE-APPROVAL',
            color: '#17A2B8'
        };
        this.status[CampaignStatus.CANCELLED] = {
            value: CampaignStatus.CANCELLED,
            label: 'CANCELLED',
            realLabel: 'CANCELLED',
            color: '#D3D3D3'
        };
        this.status[CampaignStatus.PAUSED] = {
            value: CampaignStatus.PAUSED,
            label: 'PAUSED',
            realLabel: 'PAUSED',
            color: '#F2BA0F'
        };
        this.status[CampaignStatus.LIVE] = {
            value: CampaignStatus.LIVE,
            label: 'LIVE',
            realLabel: 'LIVE',
            color: '#00C851'
        };
        this.status[CampaignStatus.COMPLETED] = {
            value: CampaignStatus.COMPLETED,
            label: 'COMPLETED',
            realLabel: 'COMPLETED',
            color: '#343A40'
        };
        this.status[CampaignStatus.STOPPED] = {
            value: CampaignStatus.STOPPED,
            label: 'STOPPED',
            realLabel: 'STOPPED',
            color: '#FD7E14'
        };
        this.status[CampaignStatus.BEING_EDITED] = {
            value: CampaignStatus.BEING_EDITED,
            label: 'BEING EDITED',
            realLabel: 'BEING EDITED',
            color: '#8598ab'
        };
    }

    ngOnInit() {
        this.getUserRole();
        this.initForm();
        this.populateDataTables();
    }

    populateDataTables() {
        if (!this.isAdmin && !this.isSupplier) {
            this.getUserCampaigns();
            return;
        } else if (this.isAdmin) {
            forkJoin([
                this.getLiveApprovedCampaigns(),
                this.getPendingCampaigns(),
                this.getPausedCancelledCampaigns(),
                this.getCancelledStoppedCompletedCampaigns(),
                this.getRejectedCampaigns(),
                this.getDraftCampaigns()
            ]);
            return;
        }

        this.addFormValuesAndDataSourcesToTable();
        forkJoin([
            this.getPendingCampaigns(),
            this.getLiveApprovedCampaigns(),
            this.getPausedCancelledCampaigns(),
            this.getCancelledStoppedCompletedCampaigns(),
            this.getRejectedCampaigns()
        ]);
    }

    shouldShowStartsIn2WorkingDaysToolTip(campaign) {
        return (
            campaign?.status == CampaignStatus.APPROVED &&
            campaign?.campaignLaunchMethod == CampaignLaunchMethod.AUTOMATICALLY_PAUSE_CAMPAIGN &&
            campaign?.startsIn2WorkingDays
        );
    }

    isPendingCampaign(status) {
        return (
            status == CampaignStatus.PENDING_APPROVAL || status == CampaignStatus.PENDING_REAPPROVAL
        );
    }

    addFormValuesAndDataSourcesToTable() {
        for (let table of this.supplierTables) {
            switch (table.form.name) {
                case 'formPending': {
                    table.form.value = this.formPending;
                    table.dataSource = this.dataSourcePending;
                    break;
                }
                case 'formLiveApproved': {
                    table.form.value = this.formLiveApproved;
                    table.dataSource = this.dataSourceLiveApproved;
                    break;
                }
                case 'formPausedCancelled': {
                    table.form.value = this.formPausedCancelled;
                    table.dataSource = this.dataSourcePausedCancelled;
                    break;
                }
                case 'formCancelledStoppedCompleted': {
                    table.form.value = this.formCancelledStoppedCompleted;
                    table.dataSource = this.dataSourceCancelledStoppedCompleted;
                    break;
                }
                case 'formRejected': {
                    table.form.value = this.formRejected;
                    table.dataSource = this.dataSourceRejected;
                    break;
                }
            }
        }
    }

    getSupplierCampaigns(event, tableType, pageIndex?: number, pageSize?: number) {
        const sortEvent = event.active !== undefined ? event : null;
        const pageEvent = event.pageIndex !== undefined ? event : null;

        const currentSort = sortEvent ? sortEvent : this.getSortByTableType(tableType);
        const currentPageIndex = pageEvent ? pageEvent.pageIndex : 0;
        const currentPageSize = pageEvent ? pageEvent.pageSize : 20;

        switch (tableType) {
            case 'formPending':
                this.getPendingCampaigns(currentSort, currentPageIndex, currentPageSize);
                break;
            case 'formLiveApproved':
                this.getLiveApprovedCampaigns(currentSort, currentPageIndex, currentPageSize);
                break;
            case 'formPausedCancelled':
                this.getPausedCancelledCampaigns(currentSort, currentPageIndex, currentPageSize);
                break;
            case 'formCancelledStoppedCompleted':
                this.getCancelledStoppedCompletedCampaigns(currentSort, currentPageIndex, currentPageSize);
                break;
            case 'formRejected':
                this.getRejectedCampaigns(currentSort, currentPageIndex, currentPageSize);
                break;
        }
    }

    private getSortByTableType(tableType: string): Sort {
        switch (tableType) {
            case 'formPending':
                return this.pendingSort;
            case 'formLiveApproved':
                return this.liveApprovedSort;
            case 'formPausedCancelled':
                return this.pausedCancelledSort;
            case 'formCancelledStoppedCompleted':
                return this.cancelledStoppedCompletedSort;
            case 'formRejected':
                return this.rejectedSort;
            default:
                return { active: '', direction: '' };
        }
    }

    getUserRole() {
        const currentUser = this.authenticationService.currentUserValue;
        if (currentUser) {
            this.isAdmin = this.authenticationService.isAdmin();
            this.isSupplier = this.authenticationService.isSupplier();
        }
    }

    ngAfterViewInit() {
        this.onLoad = true;
    }

    private initForm(): void {
        if (this.isAdmin) {
            this.formLiveApproved = this.formBuilder.group({
                search: new FormControl(null),
                startDate: new FormControl(null),
                endDate: new FormControl(null)
            });
            this.formPending = this.formBuilder.group({
                search: new FormControl(null),
                startDate: new FormControl(null),
                endDate: new FormControl(null)
            });
            this.formPausedCancelled = this.formBuilder.group({
                search: new FormControl(null),
                status: new FormControl(null),
                startDate: new FormControl(null),
                endDate: new FormControl(null)
            });
            this.formRejected = this.formBuilder.group({
                search: new FormControl(null),
                startDate: new FormControl(null),
                endDate: new FormControl(null)
            });
            this.formDraft = this.formBuilder.group({
                search: new FormControl(null),
                startDate: new FormControl(null),
                endDate: new FormControl(null)
            });
            this.formCancelledStoppedCompleted = this.formBuilder.group({
                search: new FormControl(null),
                status: new FormControl(null),
                startDate: new FormControl(null),
                endDate: new FormControl(null)
            });
            return;
        }

        if (this.isSupplier) {
            this.formLiveApproved = this.formBuilder.group({
                search: new FormControl(null),
                status: new FormControl(null),
                startDate: new FormControl(null),
                endDate: new FormControl(null)
            });
            this.formPending = this.formBuilder.group({
                search: new FormControl(null),
                startDate: new FormControl(null),
                endDate: new FormControl(null)
            });
            this.formRejected = this.formBuilder.group({
                search: new FormControl(null),
                startDate: new FormControl(null),
                endDate: new FormControl(null)
            });
            this.formPausedCancelled = this.formBuilder.group({
                search: new FormControl(null),
                startDate: new FormControl(null),
                endDate: new FormControl(null)
            });
            this.formCancelledStoppedCompleted = this.formBuilder.group({
                search: new FormControl(null),
                status: new FormControl(null),
                startDate: new FormControl(null),
                endDate: new FormControl(null)
            });
            return;
        }

        this.form = this.formBuilder.group({
            search: new FormControl(null),
            status: new FormControl(null),
            startDate: new FormControl(null),
            endDate: new FormControl(null)
        });
    }

    getCampaigns(params, sort) {
        if (params.startDate != null && moment.isMoment(params.startDate))
            params.startDate = params.startDate.format('YYYY-MM-DD') + 'T00:00:00';

        if (params.endDate != null && moment.isMoment(params.endDate))
            params.endDate = params.endDate.format('YYYY-MM-DD') + 'T00:00:00';

        params.sortColumn = sort.active;
        params.sortDirection = sort.direction;

        return this.campaignService.getCampaigns(params).pipe(first());
    }

    getUserCampaigns(currentSort: Sort = this.userSort, pageIndex: number = 0, pageSize: number = 20) {
        this.userSort = currentSort;
        let params = this.form.value;
        params.size = pageSize;
        params.page = pageIndex;
        if (params.status != null && !Array.isArray(params.status)) params.status = [params.status];
        this.getCampaigns(params, currentSort).subscribe({
            next: (resp: any) => {
                this.dataSource.dataSource = new MatTableDataSource(resp.content);
                const paginator = this.paginator.toArray()[0];
                if (paginator != undefined) {
                    paginator.pageIndex = pageIndex;
                    paginator.length = resp.totalDocuments;
                }
            },
            error: error => {
                this.alertService.error('Error: ' + error, this.alertOptions);
            }
        });
    }

    getLiveApprovedCampaigns(currentSort: Sort = this.liveApprovedSort, pageIndex: number = 0, pageSize: number = 20) {
        this.liveApprovedSort = currentSort;
        let params = this.formLiveApproved.value;
        params.size = pageSize;
        params.page = pageIndex;
        params.status = params.status
            ? this.handleStatusParamsType(params.status)
            : [
                CampaignStatus.LIVE,
                CampaignStatus.PARTIALLY_LIVE,
                CampaignStatus.APPROVED
            ];
        this.getCampaigns(params, currentSort).subscribe({
            next: (resp: any) => {
                this.dataSourceLiveApproved.dataSource = new MatTableDataSource(resp.content);
                const paginator = this.paginator.toArray()[this.isSupplier ? 1 : 0];
                if (paginator != undefined) {
                    paginator.length = resp.totalDocuments;
                }
            },
            error: error => {
                this.alertService.error('Error: ' + error, this.alertOptions);
            }
        });
    }

    getPendingCampaigns(currentSort: Sort = this.pendingSort, pageIndex: number = 0, pageSize: number = 20) {
        this.pendingSort = currentSort;
        let params = this.formPending.value;
        params.size = pageSize;
        params.page = pageIndex;
        params.status = this.isSupplier
            ? [
                CampaignStatus.PENDING_APPROVAL,
                CampaignStatus.PENDING_REAPPROVAL
            ]
            : [
                CampaignStatus.PENDING_APPROVAL,
                CampaignStatus.PENDING_REAPPROVAL,
                CampaignStatus.PARTIALLY_APPROVED
            ];
        this.getCampaigns(params, currentSort).subscribe({
            next: (resp: any) => {
                this.dataSourcePending.dataSource = new MatTableDataSource(resp.content);
                const paginator = this.paginator.toArray()[this.isSupplier ? 0 : 1];
                if (paginator != undefined) {
                    paginator.length = resp.totalDocuments;
                }
            },
            error: error => {
                this.alertService.error('Error: ' + error, this.alertOptions);
            }
        });
    }

    handleStatusParamsType(paramStatus) {
        return Array.isArray(paramStatus) ? paramStatus : [paramStatus];
    }

    getPausedCancelledCampaigns(currentSort: Sort = this.pausedCancelledSort, pageIndex: number = 0, pageSize: number = 20) {
        this.pausedCancelledSort = currentSort;
        let params = this.formPausedCancelled.value;
        params.size = pageSize;
        params.page = pageIndex;
        params.status = params.status
            ? this.handleStatusParamsType(params.status)
            : this.isSupplier
                ? [CampaignStatus.PAUSED, CampaignStatus.PAUSED_PENDING_SUP_APPROVALS, CampaignStatus.BEING_EDITED]
                : [
                    CampaignStatus.CANCELLED,
                    CampaignStatus.PAUSED,
                    CampaignStatus.PAUSED_PENDING_SUP_APPROVALS,
                    CampaignStatus.BEING_EDITED
                ];
        this.getCampaigns(params, currentSort).subscribe({
            next: (resp: any) => {
                this.dataSourcePausedCancelled.dataSource = new MatTableDataSource(resp.content);
                const paginator = this.paginator.toArray()[2];
                if (paginator != undefined) {
                    paginator.length = resp.totalDocuments;
                }
            },
            error: error => {
                this.alertService.error('Error: ' + error, this.alertOptions);
            }
        });
    }

    getCancelledStoppedCompletedCampaigns(currentSort: Sort = this.cancelledStoppedCompletedSort, pageIndex: number = 0, pageSize: number = 20) {
        this.cancelledStoppedCompletedSort = currentSort;
        let params = this.formCancelledStoppedCompleted.value;
        params.size = pageSize;
        params.page = pageIndex;
        params.status =
            params.status != null
                ? !Array.isArray(params.status)
                    ? [params.status]
                    : params.status
                : this.isSupplier
                    ? [CampaignStatus.CANCELLED, CampaignStatus.STOPPED, CampaignStatus.COMPLETED]
                    : [CampaignStatus.STOPPED, CampaignStatus.COMPLETED];
        this.getCampaigns(params, currentSort).subscribe({
            next: (resp: any) => {
                this.dataSourceCancelledStoppedCompleted.dataSource = new MatTableDataSource(
                    resp.content
                );
                const paginator = this.paginator.toArray()[this.isSupplier ? 3 : 3];
                if (paginator != undefined) {
                    paginator.length = resp.totalDocuments;
                }
            },
            error: error => {
                this.alertService.error('Error: ' + error, this.alertOptions);
            }
        });
    }

    getRejectedCampaigns(currentSort: Sort = this.rejectedSort, pageIndex: number = 0, pageSize: number = 20) {
        this.rejectedSort = currentSort;
        let params = this.formRejected.value;
        params.size = pageSize;
        params.page = pageIndex;
        params.status = [CampaignStatus.REJECTED, CampaignStatus.PARTIALLY_REJECTED];
        this.getCampaigns(params, currentSort)
            .subscribe({
                next: (resp: any) => {
                    this.dataSourceRejected.dataSource = new MatTableDataSource(resp.content);
                    const paginator = this.paginator.toArray()[this.isSupplier ? 4 : 4];
                    if (paginator != undefined) {
                        paginator.length = resp.totalDocuments;
                    }
                },
                error: error => {
                    this.alertService.error('Error: ' + error, this.alertOptions);
                }
            });
    }

    getDraftCampaigns(currentSort: Sort = this.draftSort, pageIndex: number = 0, pageSize: number = 20) {
        this.draftSort = currentSort;
        let params = this.formDraft.value;
        params.size = pageSize;
        params.page = pageIndex;
        params.status = [CampaignStatus.DRAFT];
        this.getCampaigns(params, currentSort).subscribe({
            next: (resp: any) => {
                this.dataSourceDraft.dataSource = new MatTableDataSource(resp.content);
                const paginator = this.paginator.toArray()[5];
                if (paginator != undefined) {
                    paginator.length = resp.totalDocuments;
                }
            },
            error: error => {
                this.alertService.error('Error: ' + error, this.alertOptions);
            }
        });
    }

    handleAction(event: { type: CampaignAction; payload: any }) {
        switch (event.type) {
            case CampaignAction.EDIT:
                this.editCampaign(event.payload.id, event.payload.status);
                break;
            case CampaignAction.PREVIEW:
                this.markCampaignSeenLocally(event.payload.id);
                this.previewCampaign(event.payload.id);
                break;
            case CampaignAction.DELETE:
                this.deleteCampaign(event.payload.id);
                break;
            case CampaignAction.PAUSE:
                this.pauseCampaign(event.payload.dataSource, event.payload.id);
                break;
            case CampaignAction.RELAUNCH:
                this.relaunchCampaign(event.payload.dataSource, event.payload.id);
                break;
            case CampaignAction.CANCEL:
                this.cancelCampaign(event.payload.dataSource, event.payload.id);
                break;
            case CampaignAction.STOP:
                this.stopCampaign(event.payload.dataSource, event.payload.id);
                break;
            case CampaignAction.RESTORE:
                this.restorePreviousCampaignState(event.payload.id);
                break;
            case CampaignAction.APPROVE:
                this.approveCampaign(event.payload.id);
                break;
            case CampaignAction.REJECT:
                this.rejectCampaign(event.payload.id);
                break;
            case CampaignAction.PAUSE_VISUALS:
                this.pauseCampaignVisuals(event.payload.id);
                break;
            case CampaignAction.ADD_CHARGES:
                this.addChargesToCampaign(event.payload.id);
                break;
            case CampaignAction.DOWNLOAD_MEDIA:
                this.downloadCampaignMedia(event.payload.id, event.payload.title);
                break;
        }
    }

    deleteCampaign(campaignId: string) {
        const dialogRef = this.dialog.open(CampaignDeleteModalComponent, {
            width: AppConfig.dialog_width_xs,
            maxHeight: AppConfig.dialog_max_height,
            data: { campaignId }
        });
        dialogRef.afterClosed().subscribe(result => {
            this.getUserCampaigns();
        });
    }

    addChargesToCampaign(campaignId: string) {
        const dialogRef = this.dialog.open(CampaignAddChargesModalComponent, {
            width: '90%',
            height: 'fit-content',
            data: { campaignId }
        });
        dialogRef.afterClosed().subscribe(result => {
            this.populateDataTables();
        });
    }

    cancelCampaign(dataSource: any, campaignId: string) {
        const campaign = dataSource?.filteredData.find(campaign => campaign._id == campaignId);

        const startDate = campaign?.startDate;
        const endDate = campaign?.endDate;
        const status = campaign?.status;
        const startsIn2WorkingDays = campaign?.startsIn2WorkingDays;

        const dialogRef = this.dialog.open(CampaignCancelModalComponent, {
            width: AppConfig.dialog_width_xs,
            maxHeight: AppConfig.dialog_max_height,
            data: { campaignId, status, startDate, endDate, startsIn2WorkingDays }
        });

        dialogRef.afterClosed().subscribe(result => {
            this.populateDataTables();
        });
    }

    pauseCampaignVisuals(campaignId) {
        const dialogRef = this.dialog.open(CampaignPauseEditVisualsModalComponent, {
            width: AppConfig.dialog_width_xs,
            maxHeight: AppConfig.dialog_max_height
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result.navigateToStep4) {
                this.navigateToCampaignAsApproved(campaignId);
            }
        });
    }

    pauseCampaign(dataSource: any, campaignId: string) {
        const campaign = dataSource?.filteredData.find(campaign => campaign._id == campaignId);

        const startDate = campaign.startDate;
        const endDate = campaign.endDate;
        const status = campaign.status;

        // For admin users with LIVE or PARTIALLY_LIVE campaigns, show confirmation modal
        if (
            this.isAdmin &&
            (status === CampaignStatus.LIVE || status === CampaignStatus.PARTIALLY_LIVE)
        ) {
            const dialogRef = this.dialog.open(CampaignPauseModalComponent, {
                width: AppConfig.dialog_width_xs,
                maxHeight: AppConfig.dialog_max_height,
                data: { campaignId, status, startDate, endDate, isAdminLivePause: true }
            });

            dialogRef.afterClosed().subscribe(result => {
                this.populateDataTables();
            });
            return;
        }

        // For other admin cases, keep direct API call
        if (this.isAdmin) {
            this.campaignService
                .updateCampaignStatus(campaignId, CampaignStatus.PAUSED)
                .subscribe(_ => {
                    this.populateDataTables();
                });
            return;
        }

        const dialogRef = this.dialog.open(CampaignPauseModalComponent, {
            width: AppConfig.dialog_width_xs,
            maxHeight: AppConfig.dialog_max_height,
            data: { campaignId, status, startDate, endDate }
        });

        dialogRef.afterClosed().subscribe(result => {
            this.populateDataTables();
        });
    }

    stopCampaign(dataSource: any, campaignId: string) {
        const campaign = dataSource.filteredData.find(campaign => campaign._id == campaignId);
        const status = campaign.status;
        const startDate = campaign.startDate;
        const endDate = campaign.endDate;

        const dialogRef = this.dialog.open(CampaignStopModalComponent, {
            width: AppConfig.dialog_width_xs,
            maxHeight: AppConfig.dialog_max_height,
            data: { campaignId, status, startDate, endDate }
        });

        dialogRef.afterClosed().subscribe(result => {
            this.populateDataTables();
        });
    }

    async relaunchCampaign(dataSource: any, campaignId: string) {
        const campaign = dataSource?.filteredData.find(campaign => campaign._id == campaignId);
        const status = campaign.status;
        const startDate = campaign.startDate;
        const endDate = campaign.endDate;

        const canRelaunch = await this.checkIfStartsInLessThan2WorkingDays(campaignId);
        if (canRelaunch) {
            const dialogRef = this.dialog.open(CampaignRelaunchModalComponent, {
                width: AppConfig.dialog_width_xs,
                maxHeight: AppConfig.dialog_max_height,
                data: { campaignId, status, startDate, endDate }
            });

            dialogRef.afterClosed().subscribe(result => {
                this.populateDataTables();
            });
        } else {
            this.immediatelyRelaunchCampaign(campaignId);
        }
    }

    canRestorePreviousCampaignState(campaignId: string) {
        const campaign = this.dataSource?.dataSource.filteredData.find(
            campaign => campaign._id == campaignId
        );

        return campaign.status == CampaignStatus.BEING_EDITED;
    }

    async checkIfStartsInLessThan2WorkingDays(campaignId) {
        return await this.campaignService.startsInLessThan2WorkingDays(campaignId).toPromise();
    }

    immediatelyRelaunchCampaign(campaignId): void {
        this.campaignService.relaunchCampaign(campaignId).subscribe(_ => {
            this.populateDataTables();
        });
    }

    restorePreviousCampaignState(campaignId: string) {
        this.campaignService.restorePreviousCampaignState(campaignId).subscribe(result => {
            if (result) {
                this.populateDataTables();
            }
        });
    }

    editCampaign(id: string, status: string | null = null) {
        const campaign = this.dataSource?.dataSource.filteredData.find(
            campaign => campaign._id == id
        );

        const promptFreeStatuses = [CampaignStatus.REJECTED];
        const isBeingEditedCampaign = campaign?.status == CampaignStatus.BEING_EDITED;

        if (
            (isBeingEditedCampaign && campaign.relaunchStatus == CampaignStatus.LIVE) ||
            (isBeingEditedCampaign && campaign.relaunch == CampaignStatus.PARTIALLY_LIVE)
        ) {
            return this.navigateToCampaignAsLiveEditMode(id);
        }

        if (isBeingEditedCampaign) {
            this.navigateToCampaignAsPartiallyApproved(id);
            return;
        }

        if (campaign.status == CampaignStatus.DRAFT) {
            this.navigateToEditCampaign(id);
            return;
        }

        if (promptFreeStatuses.includes(status as CampaignStatus)) {
            this.campaignService
                .updateCampaignStatus(id, CampaignStatus.BEING_EDITED)
                .subscribe(_ => {
                    this.navigateToCampaignAsPartiallyApproved(id);
                });
        } else {
            const dialogRef = this.dialog.open(CampaignEditUserModalComponent, {
                width: AppConfig.dialog_width_xs,
                maxHeight: AppConfig.dialog_max_height,
                data: { status, id }
            });

            dialogRef.afterClosed().subscribe(result => {
                if (result) {
                    if (result.isPausedLive) return this.navigateToCampaignAsLiveEditMode(id);

                    this.navigateToCampaignAsPartiallyApproved(id);
                }
            });
        }
    }

    navigateToEditCampaign(id) {
        this.router.navigateByUrl('/campaign/step-1/' + id);
    }

    navigateToCampaignAsPartiallyApproved(id) {
        this.router.navigateByUrl('/campaign/step-1/' + id + '?partial-edit-mode=true');
    }

    navigateToCampaignAsLiveEditMode(id) {
        this.router.navigateByUrl('/campaign/step-1/' + id + '?live-edit-mode=true');
    }

    navigateToCampaignAsApproved(id) {
        this.router.navigateByUrl('/campaign/step-4/' + id + '?preview=true&edit-mode=true');
    }

    previewCampaign(id) {
        this.router.navigateByUrl('/campaign/step-1/' + id + '?preview=true');
    }

    private markCampaignSeenLocally(campaignId: string) {
        const tableSources = [
            this.dataSource,
            this.dataSourceLiveApproved,
            this.dataSourcePending,
            this.dataSourceDraft,
            this.dataSourceRejected,
            this.dataSourcePausedCancelled,
            this.dataSourceCancelledStoppedCompleted
        ];

        for (const ts of tableSources) {
            try {
                const ds = ts?.dataSource as MatTableDataSource<any>;
                if (ds && ds.data) {
                    let changed = false;
                    ds.data.forEach(row => {
                        if (row._id === campaignId && row.hasUnseenUpdates) {
                            row.hasUnseenUpdates = false;
                            changed = true;
                        }
                    });
                    if (changed) {
                        ds.data = [...ds.data];
                    }
                }
            } catch (e) {
                // ignore tables that aren't initialized
            }
        }
    }

    downloadCampaign(id, campaignTitle) {
        this.downloads.download(environment.assetUrl + 'download.php?cid=' + id).subscribe(blob => {
            const a = document.createElement('a');
            const objectUrl = URL.createObjectURL(blob);
            a.href = objectUrl;
            a.download = campaignTitle + '.zip';
            a.click();
            URL.revokeObjectURL(objectUrl);
        });
    }

    downloadCampaignMedia(id: string, campaignTitle: string) {
        this.loadingOverlayService.show();
        this.campaignMediaService.downloadCampaignMedia(id).subscribe({
            next: blob => {
                const a = document.createElement('a');
                const objectUrl = URL.createObjectURL(blob);
                a.href = objectUrl;
                a.download = (campaignTitle || 'campaign-media') + '.zip';
                a.click();
                URL.revokeObjectURL(objectUrl);
                this.loadingOverlayService.hide();
            },
            error: error => {
                this.loadingOverlayService.hide();
                this.alertService.error('Error downloading media: ' + error.message, this.alertOptions);
            }
        });
    }

    approveCampaign(id) {
        this.loadingOverlayService.show();
        if (this.isSupplier) {
            this.supplierService.approveCampaign(id).subscribe(() => {
                this.loadingOverlayService.hide();
                this.populateDataTables();
            });
        } else if (this.isAdmin) {
            this.campaignService.approveAdminCampaign(id, this.rejectReason).subscribe(() => {
                this.loadingOverlayService.hide();
                this.populateDataTables();
            });
        }
    }

    rejectCampaign(id) {
        const dialogRef = this.dialog.open(CampaignRejectModalComponent, {
            width: AppConfig.dialog_width_xs,
            maxHeight: AppConfig.dialog_max_height,
            data: { campaign: null }
        });
        dialogRef.afterClosed().subscribe(result => {
            this.rejectReason = result;
            if (this.rejectReason != undefined && this.rejectReason != '') {
                this.loadingOverlayService.show();
                if (this.isSupplier) {
                    this.supplierService.rejectCampaign(id, this.rejectReason).subscribe(() => {
                        this.loadingOverlayService.hide();
                        this.populateDataTables();
                    });
                } else if (this.isAdmin) {
                    this.campaignService
                        .rejectAdminCampaign(id, this.rejectReason)
                        .subscribe(() => {
                            this.loadingOverlayService.hide();
                            this.populateDataTables();
                        });
                }
            }
        });
    }

    createFilter() {
        let filterFunction = function (data: any, filter: string): boolean {
            let searchTerms = JSON.parse(filter);
            let isFilterSet = false;
            for (const col in searchTerms) {
                if (searchTerms[col].toString() !== '') {
                    isFilterSet = true;
                } else {
                    delete searchTerms[col];
                }
            }

            let nameSearch = () => {
                let found = false;
                let foundObj = {
                    title: null,
                    status: null,
                    startDate: null,
                    endDate: null
                };
                if (isFilterSet) {
                    if (searchTerms['title']) {
                        searchTerms['title']
                            .trim()
                            .toLowerCase()
                            .split(' ')
                            .forEach(word => {
                                if (
                                    data['title'] != null &&
                                    data['title'].toString().toLowerCase().indexOf(word) != -1
                                ) {
                                    foundObj.title = true;
                                } else {
                                    foundObj.title = false;
                                }
                            });
                    }
                    if (searchTerms['status']) {
                        if (
                            (data['status'] != null &&
                                searchTerms['status'] == data['status'].toLowerCase()) ||
                            (searchTerms['status'] == 'pending' &&
                                (data['status'].toLowerCase() == 'pending_sup_approval' ||
                                    data['status'].toLowerCase() == 'pending_approval' ||
                                    data['status'].toLowerCase() == 'pending_payment'))
                        ) {
                            foundObj.status = true;
                        } else {
                            foundObj.status = false;
                        }
                    }
                    if (searchTerms['startDate']) {
                        if (
                            data['products'] != null &&
                            moment(data['products'][0].startDate).isSameOrAfter(
                                moment(searchTerms['startDate'])
                            ) &&
                            isFilterSet
                        ) {
                            foundObj.startDate = true;
                        } else {
                            foundObj.startDate = false;
                        }
                    }
                    if (searchTerms['endDate']) {
                        if (
                            data['products'] != null &&
                            moment(data['products'][0].endDate).isSameOrBefore(
                                moment(searchTerms['endDate'])
                            ) &&
                            isFilterSet
                        ) {
                            foundObj.endDate = true;
                        } else {
                            foundObj.endDate = false;
                        }
                    }
                    if (
                        (foundObj.title == true || foundObj.title == null) &&
                        (foundObj.status == true || foundObj.status == null) &&
                        (foundObj.endDate == true || foundObj.endDate == null) &&
                        (foundObj.startDate == true || foundObj.startDate == null)
                    ) {
                        found = true;
                    }
                    return found;
                } else {
                    return true;
                }
            };
            return nameSearch();
        };
        return filterFunction;
    }

    filterChange(filterValuesLabel) {
        switch (filterValuesLabel) {
            case 'filterValues':
                this.getUserCampaigns(this.userSort);
                if (this.dataSource.dataSource.paginator) {
                    this.dataSource.dataSource.paginator.firstPage();
                }
                break;
            case 'filterValuesLiveApproved':
                this.getLiveApprovedCampaigns(this.liveApprovedSort);
                if (this.dataSourceLiveApproved.dataSource.paginator) {
                    this.dataSourceLiveApproved.dataSource.paginator.firstPage();
                }
                break;
            case 'filterValuesPending':
                this.getPendingCampaigns(this.pendingSort);
                if (this.dataSourcePending.dataSource.paginator) {
                    this.dataSourcePending.dataSource.paginator.firstPage();
                }
                break;
            case 'filterValuesRejected':
                this.getRejectedCampaigns(this.rejectedSort);
                if (this.dataSourceRejected.dataSource.paginator) {
                    this.dataSourceRejected.dataSource.paginator.firstPage();
                }
                break;
            case 'filterValuesCancelledStoppedCompleted':
                this.getCancelledStoppedCompletedCampaigns(this.cancelledStoppedCompletedSort);
                if (this.dataSourceCancelledStoppedCompleted.dataSource.paginator) {
                    this.dataSourceCancelledStoppedCompleted.dataSource.paginator.firstPage();
                }
                break;
            case 'filterValuesDraft':
                this.getDraftCampaigns(this.draftSort);
                if (this.dataSourceDraft.dataSource.paginator) {
                    this.dataSourceDraft.dataSource.paginator.firstPage();
                }
                break;

            case 'filterValuesPausedCancelled':
                this.getPausedCancelledCampaigns(this.pausedCancelledSort);
                if (this.dataSourcePausedCancelled.dataSource.paginator) {
                    this.dataSourcePausedCancelled.dataSource.paginator.firstPage();
                }
        }
    }

    hasFiltersEnabled(form) {
        let params = form.value;
        return (
            params.startDate != null ||
            params.endDate != null ||
            params.search != null ||
            params.status != null
        );
    }

    resetFilters(form, filterValuesLabel) {
        if (this.hasFiltersEnabled(form)) {
            form.reset();
            switch (filterValuesLabel) {
                case 'filterValues':
                    this.getUserCampaigns();
                    break;
                case 'filterValuesLiveApproved':
                    this.getLiveApprovedCampaigns();
                    break;
                case 'filterValuesPending':
                    this.getPendingCampaigns();
                    break;
                case 'filterValuesRejected':
                    this.getRejectedCampaigns();
                    break;
                case 'filterValuesCancelledStoppedCompleted':
                    this.getCancelledStoppedCompletedCampaigns();
                    break;
                case 'filterValuesDraft':
                    this.getDraftCampaigns();
                    break;
                case 'filterValuesPausedCancelled':
                    this.getPausedCancelledCampaigns();
                    break;
            }
        }
    }

    buildInfoMessagePerStatus(status) {
        switch (status) {
            case CampaignStatus.APPROVED:
                return 'All suppliers approved, the campaign will go live in 2 working days';
            case CampaignStatus.PAUSED_PENDING_SUP_APPROVALS:
                return "This campaign is automatically paused until it's approved by all suppliers";
            case CampaignStatus.PENDING_APPROVAL:
            case CampaignStatus.PENDING_REAPPROVAL:
            case CampaignStatus.PAUSED:
                return 'This campaign is being edited by the client. You will be notified once the new version is submitted';
        }
    }

    buildUpcomingStatusMessage(date: Date, status: string, forSupplier = false): string {
        // A campaign can carry an upcomingStatusDate without a resolved upcomingStatus
        // (inconsistent/legacy data). Guard against it so change detection doesn't crash
        // on formatTitleCase(null) and tear down the whole table (which was blocking the
        // supplier/admin "Add MF charges" action from rendering).
        if (!date || !status) {
            return '';
        }
        if (!(date instanceof Date)) {
            const [year, month, day, hour] = date as [number, number, number, number];
            const jsMonth = month - 1;
            const jsDay = hour != 0 ? day + 1 : day;
            date = new Date(year, jsMonth, jsDay);
        }

        const monthIndex = date.getMonth() + 1;

        const day = date.getDate().toString().padStart(2, '0');
        const month = monthIndex.toString().padStart(2, '0');
        const year = date.getFullYear().toString().slice(-2);
        const statusString = formatTitleCase(status);

        return forSupplier
            ? [
                CampaignStatus.APPROVED,
                CampaignStatus.PARTIALLY_APPROVED,
                CampaignStatus.LIVE,
                CampaignStatus.PARTIALLY_LIVE
            ].includes(status as CampaignStatus)
                ? `Relaunched on ${day}/${month}/${year}`
                : `${statusString} on ${day}/${month}/${year}`
            : `Your campaign will ${[CampaignStatus.PAUSED, CampaignStatus.CANCELLED, CampaignStatus.STOPPED].includes(status as CampaignStatus) ? `become ` + statusString.toLowerCase().replace('_', ' ') : 'be relaunched '} on ${day}/${month}/${year}. For any additional changes, please contact the media connector support.`;
    }
}
